// assets/tf-sticky-atc.js
class TFStickyATC {
  constructor(root) {
    this.root = root;

    // Theme Vemus: variant picker + buy actions
    this.variantPicker = document.querySelector('tf-variant-picker');
    this.buyActions = document.querySelector('tf-buy-actions');
    this.mainAddBtn = this.buyActions?.querySelector('[data-action="add-to-bag"]') || null;

    this.footer = document.querySelector('#tfs-footer');
    this._updateBottomOffset = this._updateBottomOffset.bind(this);

    // Main quantity input
    this.mainQtyInput =
      document.querySelector('input[name="quantity"]') ||
      document.querySelector('[data-quantity-input]') ||
      null;

    // Sticky elements
    this.imgEl = root.querySelector('[data-tf-sticky-img]');
    this.selectionWrap = root.querySelector('[data-tf-sticky-selection]');
    this.qty = root.querySelector('[data-tf-sticky-qty]');
    this.btnMinus = root.querySelector('[data-tf-sticky-minus]');
    this.btnPlus = root.querySelector('[data-tf-sticky-plus]');
    this.btnAdd = root.querySelector('[data-tf-sticky-add]');

    if (!this.variantPicker || !this.buyActions || !this.qty || !this.btnAdd) return;

    this.variants = this._readJSON(this.variantPicker, 'script[data-variants]') || [];
    this.moneyFormat = this.variantPicker.getAttribute('data-money-format') || '{{amount}}';

    this._initUI();
    this._bind();
    this._observeVisibility();
    this._bindBottomOffset();
  }

  // ---------- helpers ----------
  _readJSON(scopeEl, selector) {
    const el = scopeEl.querySelector(selector);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent || '');
    } catch (_) {
      return null;
    }
  }

  _formatMoney(cents) {
    const fmt = this.moneyFormat || '{{amount}}';

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, fmt);
    }

    const value = Number(cents) || 0;

    const formatWithDelimiters = (amountCents, precision, thousands, decimal) => {
      const number = (amountCents / 100).toFixed(precision);
      const parts = number.split('.');
      const dollars = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      const centsPart = parts[1] ? (decimal + parts[1]) : '';
      return dollars + centsPart;
    };

    let token = 'amount';
    const m = fmt.match(/\{\{\s*(amount|amount_no_decimals|amount_with_comma_separator|amount_no_decimals_with_comma_separator)\s*\}\}/);
    if (m && m[1]) token = m[1];

    let formattedNumber = '';
    switch (token) {
      case 'amount':
        formattedNumber = formatWithDelimiters(value, 2, ',', '.');
        break;
      case 'amount_no_decimals':
        formattedNumber = formatWithDelimiters(value, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        formattedNumber = formatWithDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formattedNumber = formatWithDelimiters(value, 0, '.', ',');
        break;
      default:
        formattedNumber = formatWithDelimiters(value, 2, ',', '.');
        break;
    }

    if (!m) return formattedNumber;
    return fmt.replace(m[0], formattedNumber);
  }

  _escape(str) {
    return String(str)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  _getCurrentVariantId() {
    const idInput =
      document.querySelector('input[name="id"]') ||
      this.variantPicker.querySelector('[data-variant-id]');
    return idInput?.value ? String(idInput.value) : null;
  }

  _findVariantById(id) {
    const vid = Number(id);
    return this.variants.find((v) => Number(v.id) === vid) || null;
  }

  _getCurrentMainImage() {
    const currentId = this._getCurrentVariantId();
    const variant = this._findVariantById(currentId);

    if (variant?.featured_image?.src) {
      return variant.featured_image.src;
    }

    const productRoot =
      document.querySelector('[data-product-root]') ||
      document.querySelector('.tf-product-info-wrap') ||
      document.querySelector('.tf-main-product') ||
      document.querySelector('main');

    const img =
      productRoot?.querySelector('.tf-product-media-main img') ||
      productRoot?.querySelector('.product__media img') ||
      productRoot?.querySelector('.swiper-slide-active img') ||
      null;

    return img ? (img.currentSrc || img.src) : '';
  }

  _setStickyVisible(show) {
    if (show) {
      this.root.hidden = false;
      this.root.classList.add('show');
    } else {
      this.root.classList.remove('show');
      this.root.hidden = true;
    }
    requestAnimationFrame(this._updateBottomOffset);
  }

  // ---------- init UI ----------
  _initUI() {
    if (this.imgEl) {
      const src = this._getCurrentMainImage();
      if (src) this.imgEl.src = src;
    }

    // ring-size-selector.js auto-picks the first size on DOMContentLoaded,
    // and (being a deferred script earlier in the page) runs before this
    // one -- so its initial ring-size:change event fires before we've
    // registered a listener for it. Read the value it already set directly
    // instead of waiting for a future event that already happened.
    const ringValueEl = this.selectionWrap?.querySelector('[data-sticky-ring-size-value]');
    if (ringValueEl) {
      const currentRingInput = document.querySelector(
        '[data-ring-size-selector] [data-ring-size-custom-property]'
      );
      if (currentRingInput?.value) ringValueEl.textContent = currentRingInput.value;
    }

    const q = this.mainQtyInput ? parseInt(this.mainQtyInput.value || '1', 10) : 1;
    this.qty.value = String(Number.isFinite(q) && q > 0 ? q : 1);
  }

  // ---------- sync qty ----------
  _setMainQty(qty) {
    const q = Number(qty);
    const safe = Number.isFinite(q) && q > 0 ? q : 1;

    if (this.mainQtyInput) {
      this.mainQtyInput.value = String(safe);
      this.mainQtyInput.dispatchEvent(new Event('input', { bubbles: true }));
      this.mainQtyInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.qty.value = String(safe);
  }

  // ---------- events ----------
  _bind() {
    this.btnMinus?.addEventListener('click', () => {
      const v = parseInt(this.qty.value || '1', 10) || 1;
      this._setMainQty(Math.max(1, v - 1));
    });

    this.btnPlus?.addEventListener('click', () => {
      const v = parseInt(this.qty.value || '1', 10) || 1;
      this._setMainQty(v + 1);
    });

    this.qty.addEventListener('input', () => {
      const v = parseInt(this.qty.value || '1', 10);
      this._setMainQty(Number.isFinite(v) && v > 0 ? v : 1);
    });

    this.btnAdd.addEventListener('click', () => {
      const qty = parseInt(this.qty.value || '1', 10) || 1;
      this._setMainQty(qty);

      if (this.mainAddBtn) this.mainAddBtn.click();
    });

    // The sticky bar has no selection controls of its own -- it just
    // mirrors whatever is currently picked in the main variant picker /
    // ring size selector, so it can never fall out of sync with what
    // "Add to cart" actually adds.
    document.addEventListener('variant:change', (evt) => {
      const variant = evt?.detail?.variant;

      if (this.selectionWrap && variant) {
        const chips = this.selectionWrap.querySelectorAll('[data-sticky-option-index]');
        chips.forEach((chip) => {
          const idx = parseInt(chip.dataset.stickyOptionIndex, 10);
          const val = idx === 0 ? variant.option1 : idx === 1 ? variant.option2 : variant.option3;
          if (val) {
            const valEl = chip.querySelector('.chip-value');
            if (valEl) valEl.textContent = val;
          }
        });
      }

      if (this.imgEl) {
        const src = this._getCurrentMainImage();
        if (src) this.imgEl.src = src;
      }
    });

    document.addEventListener('ring-size:change', (evt) => {
      const val = evt?.detail?.value;
      if (!val || !this.selectionWrap) return;

      const valEl = this.selectionWrap.querySelector('[data-sticky-ring-size-value]');
      if (valEl) valEl.textContent = val;
    });

    if (this.mainQtyInput) {
      const syncQty = () => {
        const q = parseInt(this.mainQtyInput.value || '1', 10);
        this.qty.value = String(Number.isFinite(q) && q > 0 ? q : 1);
      };

      this.mainQtyInput.addEventListener('input', syncQty);
      this.mainQtyInput.addEventListener('change', syncQty);
    }
  }

  // ---------- show/hide ----------
  _observeVisibility() {
    const target = this.mainAddBtn || this.buyActions;
    if (!target) return;

    const io = new IntersectionObserver(
      (entries) => {
        const inView = entries[0]?.isIntersecting;
        this._setStickyVisible(!inView);
      },
      { threshold: 0.15 }
    );

    io.observe(target);
    this._io = io;
  }

  // ---------- bottom offset ----------
  _isVisible(el) {
    if (!el) return false;

    const style = window.getComputedStyle(el);
    if (
      style.display === 'none' ||
      style.visibility === 'hidden' ||
      Number(style.opacity) === 0
    ) {
      return false;
    }

    return el.getBoundingClientRect().height > 0;
  }


  // ---------- bottom offset ----------
  _updateBottomOffset() {
    const stickyEl =
      this.root.closest('.tf-sticky-btn-atc') ||
      document.querySelector('.tf-sticky-btn-atc') ||
      this.root;

    const footer = this.footer || document.querySelector('#tfs-footer');

    const goTop =
      document.querySelector('[id^="goTop-"]') ||
      document.querySelector('.go-top') ||
      document.querySelector('.scroll-top') ||
      document.querySelector('.scrollTop');

    const toolbar = document.querySelector('.toolbar-mobile_section');

    if (!stickyEl) return;

    const stickyHeight =
      stickyEl.offsetHeight ||
      stickyEl.scrollHeight ||
      stickyEl.getBoundingClientRect().height ||
      0;

    const isStickyVisible =
      !stickyEl.hidden &&
      stickyEl.classList.contains('show') &&
      stickyHeight > 0;

    const isMobileToolbar = window.matchMedia('(max-width: 1199px)').matches;
    const toolbarHeight =
      isMobileToolbar && this._isVisible(toolbar)
        ? toolbar.getBoundingClientRect().height
        : 0;

    const totalBottomHeight =
      (isStickyVisible ? stickyHeight : 0) + toolbarHeight;

    if (isMobileToolbar) {
      stickyEl.style.bottom = `${toolbarHeight}px`;

      if (footer) {
        footer.style.paddingBottom = totalBottomHeight ? `${totalBottomHeight}px` : '';
      }

      if (goTop) {
        goTop.style.bottom = totalBottomHeight ? `${totalBottomHeight + 10}px` : '';
      }
    } else {
      stickyEl.style.bottom = '';

      if (footer) {
        footer.style.paddingBottom = isStickyVisible ? `${stickyHeight}px` : '';
      }

      if (goTop) {
        goTop.style.bottom = isStickyVisible ? `${stickyHeight + 10}px` : '';
      }
    }
  }

  _bindBottomOffset() {
    window.addEventListener('load', this._updateBottomOffset);
    window.addEventListener('resize', this._updateBottomOffset);

    document.addEventListener('variant:change', this._updateBottomOffset);

    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:section:load', this._updateBottomOffset);
      document.addEventListener('shopify:section:select', this._updateBottomOffset);
    }
  }

  // ---------- static mount ----------
  static mountAll(scope = document) {
    scope.querySelectorAll('[data-tf-sticky-atc]').forEach((el) => {
      if (el.__tfStickyMounted) return;
      el.__tfStickyMounted = true;
      new TFStickyATC(el);
    });
  }
}

// mount
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => TFStickyATC.mountAll(), { once: true });
} else {
  TFStickyATC.mountAll();
}

// theme editor
document.addEventListener('shopify:section:load', (e) => {
  TFStickyATC.mountAll(e.target);
});
